import {
  computed,
  Injectable,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { Client } from '@shared/models';
import { HeaderService } from '@shared/services';
import { QueryOptions } from 'odata-query';
import { map, Observable, Subject, tap } from 'rxjs';
import { GstarClient } from '../../../web-api-client';
import { mapSecurityAuditHistoryResponseFromResponseNested } from '../../admin/mappers';
import { Collection } from '../../admin/models';
import { SecurityAudit } from '../../portfolio/models/security-audit';

type AuditHistoryGrid = Partial<QueryOptions<SecurityAudit>>;

@Injectable({
  providedIn: 'root',
})
export class SecurityAuditService {
  private readonly gstarClient = inject(GstarClient);
  private readonly headerService = inject(HeaderService);

  selectedClient: WritableSignal<Client | undefined> = signal(undefined);

  query: WritableSignal<AuditHistoryGrid> = signal(
    {},
    {
      equal: (_a: AuditHistoryGrid, _b: AuditHistoryGrid) => false,
    }
  );

  isFirst = computed<boolean>(() => {
    return Object.entries(this.query()).length === 0;
  });

  auditSecuritiesByClient: Subject<Collection<SecurityAudit>> = new Subject();

  loadAuditSecurities(
    query: AuditHistoryGrid
  ): Observable<Collection<SecurityAudit>> {
    return this.gstarClient
      .securitiesaudithistory(
        undefined,
        buildQueryFilter(query),
        query.top?.toString(),
        query.skip?.toString(),
        query.orderBy?.toString(),
        query.count?.toString(),
        undefined
      )
      .pipe(
        map(response => ({
          total: response.odataCount ?? 0,
          items:
            response.value?.map(response =>
              mapSecurityAuditHistoryResponseFromResponseNested(response)
            ) ?? [],
        })),
        tap((auditSecurities: Collection<SecurityAudit>) => {
          this.auditSecuritiesByClient.next(auditSecurities);
        })
      );
  }

  updateQuery(query: AuditHistoryGrid): void {
    this.query.set(query);
    this.updateAuditSecurities().subscribe();
  }

  updateClient(client: Client | undefined): void {
    this.selectedClient.set(client);
  }

  private evaluateSelectedClient(query: AuditHistoryGrid): void {
    const clientIdKey = 'security/entity/client/clientId';
    const filters = new Map(Object.entries(<JSONObject>query.filter ?? {}));

    if (this.headerService.selectedClient()) {
      filters.set(clientIdKey, this.headerService.selectedClient()!.id);
    } else {
      if (filters.has(clientIdKey)) {
        filters.delete(clientIdKey);
      }
    }

    if (filters.size > 0) {
      query.filter = Object.fromEntries(filters);
    } else {
      delete query.filter;
    }
  }

  updateAuditSecurities(): Observable<Collection<SecurityAudit>> {
    const builtQuery: AuditHistoryGrid = this.query();
    this.evaluateSelectedClient(builtQuery);
    return this.loadAuditSecurities(builtQuery);
  }
}
